import {
  Box,
  Button,
  IconButton,
  Modal,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import { DateTime } from "luxon";
import ScheduleRONComponent from "../permitComponents/contractor/ScheduleRONComponent";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { updatePermitPackage } from "../../store/actions/dataActions";
import { useNavigate } from "react-router-dom";
import { convertTimezoneFromObj } from "../../functions/helperFunctions";

const ScheduleContractorRonModal = ({
  open,
  setOpen,
  selectedRow,
  setRonStarted,
  setSnackOpen,
  ownerData,
  ownerZone,
  fiveMinBeforeSession = null,
  targetSigner = "contractor",
  isReschedule = false,
}) => {
  const navigate = useNavigate();
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession]);
  const guestUser = useSelector((state) => state.guestSession);

  const dispatch = useDispatch();
  const [scheduledDate, setScheduledDate] = useState(null);
  const [isScheduled, setIsScheduled] = useState(false);
  const [ronDate, setRonDate] = useState("");
  const [sessionUrl, setSessionUrl] = useState(false);
  const [onlySign, setOnlySign] = useState(false);
  const [notarySource, setNotarySource] = useState("permitRockstar");
  const [hasOrgNotaries, setHasOrgNotaries] = useState(false);
  const [hasRonOrgNotaries, setHasRonOrgNotaries] = useState(false);
  const [orgName, setOrgName] = useState("");

  const [isContractorIpen, setIsContractorIpen] = useState(false);
  const [contractorIpenPin, setContractorIpenPin] = useState("");
  const FINALIZING_WARNING =
    "** Warning ** Please do not close, refresh, or leave this page while we finalize your RON appointment. This can take up to a minute.";

  const instantRonStartedRef = useRef(null);

  const base = useMemo(
    () => ({
      instantError: "",
      isInstantAvailable: false,
      checkInstantLoading: false,
      expeditedSelectedBtn: false,
      furtherSelectedBtn: false,
      yesLoading: false,
      instantData: {},
      furtherData: {},
      instantTimeLeft: 60,
      startInstantTimer: false,
      immediateRequested: false,
      instantAccepted: false,
      canceledInstant: false,
      immediateLock: false,
      submitting: false,
      slots: [],
      checkDateLoading: false,
      startTimer: false,
      furtherRequested: false,
      scheduleTimeLeft: 60,
      isAvailable: false,
      furtherError: "",
      tempDate: null,
      tempTime: null,
      unavailableText: "",
      furtherDateObj: null,
      isFurtherAvailable: false,
      slotIndex: -1,
      firstTimer: true,
      submitError: "",
      timeUpTriggered: false,
      furtherTimeUpTriggered: false,
    }),
    [],
  );

  const [schedule, setSchedule] = useState(base);

  const userTimezone = useMemo(() => {
    if (currentSession === "guestSession") {
      return (
        ownerZone ||
        guestUser?.registerData?.timezone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone ||
        "America/New_York"
      );
    }

    return (
      currentUser?.subuser?.timezone ||
      currentUser?.user?.personal?.[0]?.timezone ||
      currentUser?.user?.personal?.[0]?.timeZone ||
      currentUser?.timezone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone ||
      "America/New_York"
    );
  }, [currentSession, currentUser, guestUser, ownerZone]);

  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const tabletSize = useMediaQuery((theme) => theme.breakpoints.down("md"));

  // Check if current user is an additional contractor
  const getCurrentUserAdditionalContractor = useCallback(() => {
    const userEmail = (
      currentUser?.user?.subuser?.email ||
      currentUser?.user?.user?.email ||
      currentUser?.user?.email ||
      ""
    ).toLowerCase();

    if (!userEmail) return null;

    const additionalContractors =
      selectedRow?.processing_permit?.notarization?.additional_contractors ||
      [];

    return additionalContractors.find(
      (ac) => ac?.email?.toLowerCase() === userEmail,
    );
  }, [
    currentUser,
    selectedRow?.processing_permit?.notarization?.additional_contractors,
  ]);

  const additionalContractorData = getCurrentUserAdditionalContractor();

  const assigningContractorInstantToken = useMemo(() => {
    const assigningContractors =
      selectedRow?.processing_permit?.notarization?.assigning_contractors || [];

    const userEmail = (
      currentUser?.user?.subuser?.email ||
      currentUser?.user?.user?.email ||
      currentUser?.user?.email ||
      ""
    ).toLowerCase();

    if (!userEmail) return "";

    const matched = assigningContractors.find(
      (ac) => (ac?.contractor_email || "").toLowerCase() === userEmail,
    );

    return matched?.notarization?.instant_token || "";
  }, [
    currentUser,
    selectedRow?.processing_permit?.notarization?.assigning_contractors,
  ]);

  const assigningContractorData = useMemo(() => {
    const assigningContractors =
      selectedRow?.processing_permit?.notarization?.assigning_contractors || [];

    const userEmail = (
      currentUser?.user?.subuser?.email ||
      currentUser?.user?.user?.email ||
      currentUser?.user?.email ||
      ""
    ).toLowerCase();

    if (!userEmail) return null;

    return assigningContractors.find(
      (ac) => (ac?.contractor_email || "").toLowerCase() === userEmail,
    );
  }, [
    currentUser,
    selectedRow?.processing_permit?.notarization?.assigning_contractors,
  ]);

  const currentContractorEntry =
    assigningContractorData || additionalContractorData;

  // Use assigning contractor first, then additional contractor, otherwise main contractor
  const contractorNotarization = currentContractorEntry
    ? currentContractorEntry?.notarization
    : selectedRow?.processing_permit?.notarization?.contractor?.notarization;

  const ownerNotarization =
    selectedRow?.processing_permit?.notarization?.owner?.notarization;

  const activeNotarization =
    targetSigner === "owner" ? ownerNotarization : contractorNotarization;

  const cancelRef = useRef(false);
  const handleTimeUpCalledRef = useRef(false); // 🚫 Prevent reentry
  const instantRonAcceptedRef = useRef(false);
  const finalizedResetKeyRef = useRef("");
  const guestCompletionHandledRef = useRef(false);

  const bookedAppointment =
    activeNotarization?.appointments?.booked?.[0] || null;
  const bookedAppointmentKey = useMemo(() => {
    if (!bookedAppointment) return "";
    return [
      bookedAppointment?.year,
      bookedAppointment?.month,
      bookedAppointment?.day_of_month,
      bookedAppointment?.time,
    ].join("-");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bookedAppointment?.year,
    bookedAppointment?.month,
    bookedAppointment?.day_of_month,
    bookedAppointment?.time,
  ]);
  const signerUrl = activeNotarization?.bn_session?.signer_url || "";
  const waitingToSchedule = activeNotarization?.waiting_to_schedule;
  const userZoneForDisplay = useMemo(
    () =>
      currentUser?.subuser?.timezone ||
      currentUser?.user?.personal?.[0]?.timezone ||
      userTimezone ||
      "America/New_York",
    [currentUser?.subuser?.timezone, currentUser?.user?.personal, userTimezone],
  );

  const dispatchInitialRon = useCallback(async () => {
    let actionModule;
    switch (currentSession) {
      case "subUserSession":
        actionModule = await import("../../store/actions/subUserActions");
        break;
      case "guestSession":
        actionModule = await import("../../store/actions/guestActions");
        break;
      case "privateProviderSession":
        actionModule = await import(
          "../../store/actions/privateProviderActions"
        );
        break;
      default:
        actionModule = await import("../../store/actions/userActions");
        break;
    }

    if (actionModule?.setInstantRonInitialState) {
      dispatch(actionModule.setInstantRonInitialState());
    }
  }, [currentSession, dispatch]);

  const refreshGuestManageData = useCallback(async () => {
    if (currentSession !== "guestSession") return;

    const guestActions = await import("../../store/actions/guestActions");
    if (guestActions?.fetchGuestData) {
      await dispatch(guestActions.fetchGuestData("guestSession"));
    }
  }, [currentSession, dispatch]);

  useEffect(() => {
    if (activeNotarization?.only_sign && onlySign === false) {
      setOnlySign(true);
    } else if (!activeNotarization?.only_sign && onlySign === true) {
      setOnlySign(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeNotarization?.only_sign]);

  // Reconcile modal state when package updates are pushed from websocket/background processing.
  // This handles the case where a notary completes scheduling from Expired Requests while
  // this modal is open on the contractor/user side.
  useEffect(() => {
    if (!open) return;

    if (waitingToSchedule === false && bookedAppointmentKey) {
      const booked0 = activeNotarization?.appointments?.booked?.[0];
      if (!booked0) return;

      const dateString = convertTimezoneFromObj(
        booked0,
        userZoneForDisplay,
        "cccc, LLLL dd, yyyy hh:mm a",
      );

      setRonDate((prev) => (prev === dateString ? prev : dateString));
      setSessionUrl((prev) => {
        const nextUrl = signerUrl || false;
        return prev === nextUrl ? prev : nextUrl;
      });
      setIsScheduled((prev) => (prev === true ? prev : true));

      // stop timers / pending instant request UI if it is still running
      setSchedule((prev) => {
        const shouldResetSchedule =
          prev?.startInstantTimer ||
          prev?.startTimer ||
          prev?.immediateRequested ||
          prev?.furtherRequested ||
          prev?.immediateLock ||
          prev?.instantAccepted ||
          prev?.submitError !== "" ||
          prev?.isInstantAvailable ||
          prev?.isFurtherAvailable ||
          prev?.expeditedSelectedBtn ||
          prev?.furtherSelectedBtn;

        if (!shouldResetSchedule) {
          return prev;
        }

        return {
          ...base,
          instantError: prev?.instantError || "",
          submitError: "",
        };
      });

      // Clear stale acceptance signal once scheduling is finalized
      const finalizedResetKey = `${selectedRow?.task_id || ""}:${selectedRow?.cst_packageID || ""}:${bookedAppointmentKey}:${signerUrl}`;
      if (finalizedResetKeyRef.current !== finalizedResetKey) {
        finalizedResetKeyRef.current = finalizedResetKey;
        void dispatchInitialRon();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    waitingToSchedule,
    bookedAppointmentKey,
    signerUrl,
    userZoneForDisplay,
    base,
    dispatchInitialRon,
    selectedRow?.task_id,
    selectedRow?.cst_packageID,
  ]);

  useEffect(() => {
    if (activeNotarization?.is_ipen) {
      setIsContractorIpen(true);
      setContractorIpenPin(activeNotarization?.ipen_pin || "");
    } else {
      setIsContractorIpen(false);
      setContractorIpenPin("");
    }
  }, [activeNotarization?.is_ipen, activeNotarization?.ipen_pin]);

  useEffect(() => {
    if (schedule?.instantError) {
      const timer = setTimeout(() => {
        setSchedule((prev) => ({
          ...prev,
          instantError: "",
        }));
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [schedule?.instantError]);

  //! FINAL //////////////////////////////////////////////////////
  const startInstantRon = useCallback(async () => {
    setSchedule((prev) => ({
      ...prev,
      submitError: FINALIZING_WARNING,
    }));

    let payload = null;
    if (ownerData) {
      payload = {
        task_id: selectedRow?.task_id,
        cst_packageID: selectedRow?.cst_packageID,
        signeeEmail: ownerData["email"],
        currentSession: currentSession,
        name: ownerData["name"],
        signatoryTitle: ownerData["signatoryTitle"],
        authority: ownerData["authority"],
        phone: ownerData["phone"],
        ownerAddress: ownerData["ownerAddress"],
        address: ownerData["address"],
        city: ownerData["city"],
        state: ownerData["state"],
        zipCode: ownerData["zipCode"],
        owners: ownerData["owners"],
        dob: "",
        idType: ownerData["idType"],
        timezone: ownerZone,
        notarySource: notarySource,
        isReschedule,
      };
    } else {
      payload = {
        task_id: selectedRow?.task_id,
        cst_packageID: selectedRow?.cst_packageID,
        currentSession: currentSession,
        timezone: userTimezone,
        notarySource: notarySource,
        isReschedule,
      };
    }

    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/start-instant-contractor-ron`,
        { payload, instant_token: assigningContractorInstantToken },
        { withCredentials: true },
      );

      if (resp.data.success === true) {
        await dispatchInitialRon();

        if (["guestSession"].includes(currentSession)) {
          await refreshGuestManageData();
          navigate("/signee/manage");
          return;
        }
        setSchedule((prev) => ({
          ...prev,
          instantError: "",
          startInstantTimer: false,
          immediateRequested: false,
        }));
        const permit = resp?.data?.data;
        await dispatch(updatePermitPackage(permit));

        const userZone = currentUser?.subuser
          ? currentUser?.subuser?.timezone
          : currentUser?.user?.personal?.[0]?.timezone;

        const notarizationBase =
          targetSigner === "owner"
            ? permit?.processing_permit?.notarization?.owner?.notarization
            : currentContractorEntry?.notarization ||
              permit?.processing_permit?.notarization?.contractor?.notarization;

        const dateString = convertTimezoneFromObj(
          notarizationBase?.appointments?.booked?.[0],
          userZone || "America/New_York",
          "cccc, LLLL dd, yyyy hh:mm a",
        );
        setRonDate(dateString);
        setSessionUrl(notarizationBase?.bn_session?.signer_url || false);
        setIsScheduled(true);
        setRonStarted(true);
        setSnackOpen(true);
        setSchedule(base);
      } else {
        // allow re-try when acceptance signal is received again
        instantRonStartedRef.current = false;
        setSchedule((prev) => ({
          ...prev,
          instantAccepted: false,
          immediateLock: false,
          submitError: "",
          immediateRequested: false, // Reset submitting state on error
        }));
        dispatchInitialRon();
      }
    } catch (e) {
      // allow re-try when acceptance signal is received again
      instantRonStartedRef.current = false;
      setSchedule((prev) => ({
        ...prev,
        submitError: "",
        immediateRequested: false, // Reset submitting state on exception
      }));
      if (e?.response?.data?.error === "stop") {
        setSchedule((prev) => ({
          ...prev,
          slots: [],
          slotIndex: -1,
          isInstantAvailable: false,
          instantAccepted: false,
          checkInstantLoading: false,
          isAvailable: false,
          instantData: {},
          firstTimer: true,
          instantError: e?.response?.data?.message,
          immediateRequested: false,
          canceledInstant: false,
          immediateLock: false,
          startInstantTimer: false,
          instantTimeLeft: 0,
        }));
        dispatchInitialRon();
      } else {
        dispatchInitialRon();
        setSchedule((prev) => ({
          ...prev,
          immediateLock: false,
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ownerData,
    ownerZone,
    selectedRow?.task_id,
    selectedRow?.cst_packageID,
    currentSession,
    currentUser,
    dispatch,
    navigate,
    dispatchInitialRon,
    refreshGuestManageData,
    setSnackOpen,
    setRonStarted,
    base,
    notarySource,
    isReschedule,
    assigningContractorInstantToken,
    userTimezone,
    FINALIZING_WARNING,
  ]);
  //! //////////////////////////////////////////////////////////////

  //! EXPEDITED RON FUNC
  const handleImmediateRON = async () => {
    if (schedule?.isInstantAvailable) {
      setSchedule((prev) => ({
        ...prev,
        checkInstantLoading: true,
      }));
      try {
        const payload = {
          task_id: selectedRow?.task_id,
          currentSession: currentSession,
          instantData: schedule?.instantData,
          instant: true,
          timezone: userTimezone,
          cst_packageID: selectedRow?.cst_packageID,
          notarySource: notarySource,
        };

        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/notary/request-immediate-ron`,
          {
            payload,
          },
          {
            withCredentials: true,
          },
        );
        if (resp.data.success) {
          // Reset timer ref when starting new instant timer
          handleTimeUpCalledRef.current = false;

          setSchedule((prev) => ({
            ...prev,
            instantTimeLeft: 300,
            startInstantTimer: true,
            immediateRequested: true,
          }));
        }
      } catch (e) {}
      setSchedule((prev) => ({
        ...prev,
        checkInstantLoading: false,
      }));
    } else {
      setSchedule((prev) => ({
        ...prev,
        checkInstantLoading: true,
        yesLoading: true,
      }));
      setScheduledDate(null);

      const dateTime = DateTime.now().setZone(userTimezone);

      const formattedObject = {
        day_of_month: dateTime.day,
        month: dateTime.month,
        year: dateTime.year,
        time: dateTime.toFormat("HH:mm"),
      };

      const payload = {
        task_id: selectedRow?.task_id,
        cst_packageID: selectedRow?.cst_packageID,
        ask_date: formattedObject,
        instant: true,
        timezone: userTimezone,
        currentSession: currentSession,
        notarySource: notarySource,
      };

      try {
        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/notary/available-ron-schedule`,
          {
            payload,
          },
          {
            withCredentials: true,
          },
        );
        if (resp.data.success) {
          // Reset timer ref when starting new instant timer
          handleTimeUpCalledRef.current = false;

          setSchedule((prev) => ({
            ...prev,
            isInstantAvailable: true,
            instantData: resp.data,
            // immediateRequested: true,
            instantTimeLeft: 60,
            startInstantTimer: true,
          }));
        } else {
          setSchedule((prev) => ({
            ...prev,
            instantError: resp.data.message,
          }));
        }
      } catch (e) {
        if (e?.response?.data?.action === "redirect_owner") {
          navigate("/signee/manage");
        }
      } finally {
        setSchedule((prev) => ({
          ...prev,
          checkInstantLoading: false,
          yesLoading: false,
        }));
      }
    }
  };
  //! /////////////////////

  //! FURTHER DATE ////////////////////////////////////
  const handleStartClick = async () => {
    if (schedule?.isFurtherAvailable) {
      setSchedule((prev) => ({
        ...prev,
        checkDateLoading: true,
      }));

      try {
        const payload = {
          task_id: selectedRow?.task_id,
          currentSession: currentSession,
          instantData: schedule?.furtherData,
          instant: true,
          timezone: userTimezone,
          further: schedule?.furtherSelectedBtn,
          cst_packageID: selectedRow?.cst_packageID,
          notarySource: notarySource,
        };
        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/notary/request-immediate-ron`,
          {
            payload,
          },
          {
            withCredentials: true,
          },
        );

        if (resp.data.success) {
          setSchedule((prev) => ({
            ...prev,
            scheduleTimeLeft: 300,
            startTimer: true,
            furtherRequested: true,
          }));
        }
      } catch (e) {}
      setSchedule((prev) => ({
        ...prev,
        checkDateLoading: false,
      }));
    } else {
      setSchedule((prev) => ({
        ...prev,
        checkDateLoading: true,
      }));
      setScheduledDate(null);
      const payload = {
        task_id: selectedRow?.task_id,
        cst_packageID: selectedRow?.cst_packageID,
        ask_date: schedule?.furtherDateObj,
        instant: true,
        timezone: userTimezone,
        currentSession: currentSession,
        is_further: schedule?.furtherSelectedBtn,
        notarySource: notarySource,
      };
      try {
        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/notary/available-ron-schedule`,
          {
            payload,
          },
          {
            withCredentials: true,
          },
        );
        if (
          resp.data.isAvailable?.length === 0 &&
          resp.data?.slots?.length > 0
        ) {
          setSchedule((prev) => ({
            ...prev,
            scheduleTimeLeft: 60,
            startTimer: true,
            instantAccepted: false,
            isFurtherAvailable: false,
            unavailableText:
              "The selected date is unavailable. Please choose a different date or select one from the list below.",
            slots: resp.data.slots,
          }));
        } else if (resp.data.success) {
          setSchedule((prev) => ({
            ...prev,
            isFurtherAvailable: true,
            furtherData: resp.data,
            scheduleTimeLeft: 60,
            startTimer: true,
            slots: [],
          }));
        } else {
          setSchedule((prev) => ({
            ...prev,
            furtherError: resp.data.message,
            scheduleTimeLeft: 60,
            firstTimer: false,
            isAvailable: false,
            instantAccepted: false,
            unavailableText:
              "The selected date is unavailable. Please choose a different date.",
            slots: [],
          }));
        }
      } catch (e) {
        if (e?.response?.data?.action === "redirect_owner") {
          navigate("/signee/manage");
        }
      } finally {
        setSchedule((prev) => ({
          ...prev,
          checkDateLoading: false,
        }));
      }
    }
  };

  const { immediateRequested, canceledInstant } = schedule || {};

  // Check both current user (contractors) and guest user (owners) for instant RON acceptance
  const contractorInstantRonAccepted = currentUser?.instantRonAccepted || false;
  const ownerInstantRonAccepted = guestUser?.instantRonAccepted || false;
  const instantRonAccepted =
    contractorInstantRonAccepted || ownerInstantRonAccepted;
  const acceptanceInProgress =
    instantRonAccepted === true ||
    instantRonAccepted === "success" ||
    schedule?.instantAccepted === true ||
    schedule?.immediateLock === true ||
    schedule?.immediateRequested === true;

  const handleTimeUp = useCallback(
    async (type = "further") => {
      // If acceptance is already in progress/accepted, never clear booked slots.
      // Clearing here can race with accept-from-expired finalization and leave
      // notary/owner flows hanging.
      if (type === "instant" && instantRonAcceptedRef.current === true) {
        setSchedule((prev) => ({
          ...prev,
          startInstantTimer: false,
          timeUpTriggered: false,
          furtherTimeUpTriggered: false,
        }));
        return;
      }

      if (
        (type === "further" && cancelRef.current) ||
        (type === "instant" && handleTimeUpCalledRef.current)
      ) {
        return;
      }

      if (type === "further") {
        cancelRef.current = true;
      } else if (type === "instant") {
        handleTimeUpCalledRef.current = true;
      }

      const payload = {
        task_id: selectedRow?.task_id,
        cst_packageID: selectedRow?.cst_packageID,
        currentSession,
      };

      try {
        setSchedule((prev) => ({
          ...prev,
          slots: [],
          slotIndex: -1,
          isInstantAvailable: false,
          isAvailable: false,
          instantData: {},
          unavailableText: "",
          firstTimer: true,
        }));
        setScheduledDate(null);

        if (immediateRequested === true) {
          if (canceledInstant === false && instantRonAccepted === false) {
            setSchedule((prev) => ({
              ...prev,
              instantError:
                "Unfortunately, we have not received any response from the notaries at this moment. We apologize for the inconvenience as they are currently very busy. Please consider scheduling an appointment for a later time. Thank you for your understanding.",
            }));
          }

          setSchedule((prev) => ({
            ...prev,
            immediateRequested: false,
            canceledInstant: false,
          }));
        }

        await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/notary/clear-ron-schedules`,
          { payload },
          { withCredentials: true },
        );
      } catch (e) {}

      setSchedule((prev) => ({
        ...prev,
        startTimer: false,
        startInstantTimer: false,
        timeUpTriggered: false,
        furtherTimeUpTriggered: false,
        immediateRequested: false,
      }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedRow?.task_id,
      selectedRow?.cst_packageID,
      setSchedule,
      setScheduledDate,
    ],
  );

  const saveExpiredRonRequest = useCallback(async () => {
    const payload = {
      task_id: selectedRow?.task_id,
      cst_packageID: selectedRow?.cst_packageID,
      currentSession,
      notarySource: notarySource,
    };

    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/save-expired-ron-request`,
        { payload },
        { withCredentials: true },
      );
    } catch (e) {}
  }, [
    selectedRow?.task_id,
    selectedRow?.cst_packageID,
    currentSession,
    notarySource,
  ]);

  useEffect(() => {
    return () => {
      cancelRef.current = false;
      handleTimeUpCalledRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (schedule?.tempDate && schedule?.tempTime) {
      // Extract values from tempDate and tempTime
      const year = schedule?.tempDate.year();
      const month = schedule?.tempDate.month() + 1; // Adjust for zero-indexed months
      const day = schedule?.tempDate.date();
      const hour = schedule?.tempTime.hour();
      const minute = schedule?.tempTime.minute();

      // Ensure all values are numbers and are valid
      if (
        !isNaN(year) &&
        !isNaN(month) &&
        !isNaN(day) &&
        !isNaN(hour) &&
        !isNaN(minute)
      ) {
        const dateObj = {
          day_of_month: day,
          month: month,
          year: year,
          time: schedule?.tempTime.format("HH:mm"), // Formats the time in 'HH:mm' format
        };

        // Convert tempDate and tempTime to the user's timezone
        const userDateTime = DateTime.fromObject(
          {
            year,
            month,
            day,
            hour,
            minute,
            second: 0, // Set seconds to 0 by default
          },
          { zone: userTimezone },
        );

        // Check if the date and time is in the past
        if (userDateTime < DateTime.now().setZone(userTimezone)) {
          setSchedule((prev) => ({
            ...prev,
            unavailableText: "Selected datetime is in the past.",
            furtherDateObj: null,
          }));
        } else {
          setSchedule((prev) => ({
            ...prev,
            furtherDateObj: dateObj,
            unavailableText: "",
          }));
          setScheduledDate(dateObj);
        }
      } else {
        // If any part of the date or time is invalid, set an appropriate message
        setSchedule((prev) => ({
          ...prev,
          unavailableText: "Invalid date or time selected.",
          furtherDateObj: null,
        }));
      }
    }
  }, [schedule?.tempDate, schedule?.tempTime, userTimezone]);

  useEffect(() => {
    let intervalId;

    if (schedule?.startTimer && schedule?.scheduleTimeLeft > 0) {
      intervalId = setInterval(() => {
        setSchedule((prev) => {
          const newTimeLeft = prev.scheduleTimeLeft - 1;
          const updates = { ...prev, scheduleTimeLeft: newTimeLeft };

          if (newTimeLeft <= 0 && cancelRef.current) {
            cancelRef.current = false;
            updates.furtherTimeUpTriggered = true;
            updates.furtherData = {};
            updates.furtherDateObj = {};
            updates.furtherSelectedBtn = false;
            updates.isFurtherAvailable = false;
            handleTimeUp("further"); // safe async
          }

          return updates;
        });
      }, 1000);
    }

    return () => clearInterval(intervalId);
  }, [schedule?.startTimer, handleTimeUp, schedule?.scheduleTimeLeft]);

  useEffect(() => {
    let intervalIdInstant;

    if (schedule?.startInstantTimer && schedule?.instantTimeLeft > 0) {
      intervalIdInstant = setInterval(() => {
        setSchedule((prev) => {
          const newTimeLeft = prev.instantTimeLeft - 1;
          const updates = { ...prev, instantTimeLeft: newTimeLeft };

          if (newTimeLeft <= 0) {
            // Always reset UI state immediately when timer expires
            updates.instantData = {};
            updates.expeditedSelectedBtn = false;
            updates.isInstantAvailable = false;
            updates.startInstantTimer = false;

            // Call cleanup function if not already called
            if (
              !handleTimeUpCalledRef.current &&
              !instantRonAcceptedRef.current
            ) {
              const shouldSaveExpiredRequest =
                prev.immediateRequested === true &&
                instantRonAcceptedRef.current === false;

              // Use setTimeout to call handleTimeUp outside the setState callback
              setTimeout(async () => {
                if (shouldSaveExpiredRequest) {
                  await saveExpiredRonRequest();
                }
                await handleTimeUp("instant");
              }, 0);
            }
          }

          return updates;
        });
      }, 1000);
    }

    return () => clearInterval(intervalIdInstant);
  }, [
    schedule?.startInstantTimer,
    handleTimeUp,
    schedule?.instantTimeLeft,
    saveExpiredRonRequest,
  ]);

  useEffect(() => {
    instantRonAcceptedRef.current = Boolean(instantRonAccepted);
  }, [instantRonAccepted]);

  useEffect(() => {
    if (
      (schedule?.scheduleTimeLeft === 0 || schedule?.scheduleTimeLeft < 0) &&
      !schedule?.furtherTimeUpTriggered &&
      !cancelRef.current
    ) {
      // LOCK before running
      cancelRef.current = true;

      // Still set the flag (optional if you’re using the ref instead)
      setSchedule((prev) => ({
        ...prev,
        furtherTimeUpTriggered: true,
      }));
      cancelRef.current = false;
      handleTimeUp("further");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    schedule?.scheduleTimeLeft,
    schedule?.furtherTimeUpTriggered,
    handleTimeUp,
  ]);

  useEffect(() => {
    if (
      schedule?.submitError !== "" &&
      schedule?.submitError !== FINALIZING_WARNING
    ) {
      setSchedule((prev) => ({
        ...prev,
        submitting: false,
      }));
      const interv = setInterval(() => {
        setSchedule((prev) => ({
          ...prev,
          submitError: "",
        }));
      }, 5000);
      return () => clearInterval(interv);
    }
  }, [schedule?.submitError, FINALIZING_WARNING]);

  useEffect(() => {
    if (
      (instantRonAccepted === true || instantRonAccepted === "success") &&
      schedule?.immediateLock === false
    ) {
      const sessionAlreadyScheduled =
        waitingToSchedule === false &&
        Boolean(bookedAppointmentKey) &&
        Boolean(signerUrl);

      if (sessionAlreadyScheduled) {
        setSchedule((prev) => ({
          ...prev,
          immediateLock: true,
          instantAccepted: true,
          submitError: FINALIZING_WARNING,
        }));
        return;
      }

      if (
        schedule?.immediateRequested === true &&
        !instantRonStartedRef?.current
      ) {
        instantRonStartedRef.current = true;
        setSchedule((prev) => ({
          ...prev,
          immediateLock: true,
          instantAccepted: true,
        }));
        startInstantRon();
      } else if (schedule?.immediateRequested !== true) {
        setSchedule((prev) => ({
          ...prev,
          immediateLock: true,
          instantAccepted: true,
          submitError: FINALIZING_WARNING,
        }));
      }
    } else if (
      schedule?.instantAccepted === true &&
      instantRonAccepted === false
    ) {
      setSchedule((prev) => ({
        ...prev,
        instantAccepted: false,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    instantRonAccepted,
    schedule?.immediateLock,
    schedule?.instantAccepted,
    schedule?.immediateRequested,
    waitingToSchedule,
    bookedAppointmentKey,
    signerUrl,
    FINALIZING_WARNING,
  ]);

  // Expired-request acceptance path (owner/guest):
  // If backend fallback completes without immediateRequested=true,
  // redirect owner to the manage page instead of leaving modal stuck.
  useEffect(() => {
    if (!open || currentSession !== "guestSession") return;

    const hasSuccessSignal = instantRonAccepted === "success";
    const hasScheduledSession =
      waitingToSchedule === false &&
      Boolean(bookedAppointmentKey) &&
      Boolean(signerUrl);

    if (
      (hasSuccessSignal || hasScheduledSession) &&
      !guestCompletionHandledRef.current
    ) {
      guestCompletionHandledRef.current = true;
      setOpen(false);
      void dispatchInitialRon();
      void refreshGuestManageData();
      navigate("/signee/manage");
    }
  }, [
    open,
    currentSession,
    instantRonAccepted,
    waitingToSchedule,
    bookedAppointmentKey,
    signerUrl,
    dispatchInitialRon,
    refreshGuestManageData,
    navigate,
    setOpen,
  ]);

  // Reset transient acceptance state on modal open/task change
  // Prevents stale instantRonAccepted from triggering an early start-instant call.
  useEffect(() => {
    if (!open) return;

    instantRonStartedRef.current = false;
    handleTimeUpCalledRef.current = false;
    cancelRef.current = false;
    finalizedResetKeyRef.current = "";
    guestCompletionHandledRef.current = false;

    setIsScheduled(false);
    setRonDate("");
    setSessionUrl(false);

    setSchedule(base);

    void dispatchInitialRon();
  }, [
    open,
    selectedRow?.task_id,
    selectedRow?.cst_packageID,
    base,
    dispatchInitialRon,
  ]);

  useEffect(() => {
    if (!open) {
      void dispatchInitialRon();
    }
  }, [open, dispatchInitialRon]);

  // Check if organization has notaries
  // For both main users and subusers, check their organization notaries
  useEffect(() => {
    let hasNotaries = false;
    let hasRonNotaries = false;
    let subscriptions = [];
    if (!guestUser) {
      // Check main user path first, then subuser path, then root tier path
      const notaryData =
        currentUser?.user?.tier?.notary ||
        currentUser?.subuser?.tier?.notary ||
        currentUser?.tier?.notary ||
        {};

      hasNotaries = (notaryData?.num_notaries || 0) > 0;
      subscriptions = notaryData?.subscriptions || [];
      setOrgName(notaryData?.orgName || "");
    } else {
      hasNotaries = (guestUser?.registerData?.orgNotaries || 0) > 0;
      subscriptions = guestUser?.registerData?.notarySubscriptions || [];
      setOrgName(guestUser?.registerData?.orgName || "");
    }

    if (hasNotaries && subscriptions.length > 0) {
      hasRonNotaries = subscriptions.some(
        (subscription) =>
          subscription.notary_type === 1 || subscription.notary_type === 2,
      );
    }

    setHasOrgNotaries(hasNotaries);
    setHasRonOrgNotaries(hasRonNotaries);
  }, [currentUser, guestUser]);

  return (
    <Modal
      open={open}
      onClose={() => {
        handleTimeUpCalledRef.current = false;
        cancelRef.current = false;
        finalizedResetKeyRef.current = "";
        setSchedule(base);
        void dispatchInitialRon();
      }}
      sx={{ overflow: "auto" }}
    >
      <Box
        sx={{
          overflowY: "none",
          position: "relative",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxHeight: "90%",
          height: mobileSize ? "80%" : "auto",
          maxWidth: "70rem",
          bgcolor: "background.paper",
          borderRadius: "18px",
          boxShadow: 24,
          px: mobileSize ? 1 : 4,
          py: 2,
          animation: "fadeIn 0.5s ease-in-out",
        }}
      >
        <Typography
          id="modal-modal-title"
          variant={mobileSize ? "h5" : "h4"}
          align="center"
          component="h2"
          color="primary"
        >
          {onlySign
            ? "Your Documents are Now Prepared and Ready for Signing"
            : isScheduled
              ? "Your Remote Online Notarization (RON) Session is Scheduled"
              : isContractorIpen
                ? "Your In-Person Electronic Notarization (IPEN) Session is Scheduled"
                : "Schedule your Remote Online Notarization (RON) Session"}
        </Typography>
        <Typography
          align="center"
          variant="body2"
          color="primary"
          fontWeight="bold"
          mb={2}
        >
          {onlySign
            ? "Please proceed to sign the documents at your convenience. Once you have signed, the owner will be able to initiate the notarization process."
            : isContractorIpen
              ? "Your In-Person Electronic Notarization (IPEN) session has been scheduled. Your assigned notary will contact you to coordinate the meeting time and location. Please have a valid government-issued photo ID ready for the appointment."
              : !isScheduled &&
                "Choose a date that is convenient for you. Once the date and time have been selected, we will contact the notaries to see if they are available."}
        </Typography>
        <Box sx={{ position: "absolute", right: 10, top: 10 }}>
          <IconButton
            disabled={acceptanceInProgress}
            onClick={() => {
              void dispatchInitialRon();
              setOpen(false);
              if (schedule?.startInstantTimer || schedule?.startTimer) {
                handleTimeUp();
              }
              setSchedule(base);
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box
          sx={{
            maxHeight: "40rem", // Adjust the max height as needed
            overflowY: "auto",
            height: mobileSize ? "calc(90vh - 280px)" : "calc(90vh - 360px)",
            border: "2px solid #d3d3d3",
            mb: 1,
            p: 2,
            borderRadius: "18px",
            boxShadow: "3px 4px 12px rgba(0, 0, 0, 0.3)", // Added box shadow
          }}
        >
          {onlySign ? (
            <Box>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <img
                  alt="avatar_circle"
                  src="/img/logos/logo_main.png"
                  style={{ width: "18rem", height: "auto", zIndex: 2 }}
                />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Button
                  component="a"
                  href={
                    selectedRow?.processing_permit?.notarization?.contractor
                      ?.notarization?.bn_session?.signer_url
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    setOpen(false);
                  }}
                  sx={{ ml: 2.5, zIndex: 1 }}
                  size="large"
                  variant="contained"
                >
                  SIGN YOUR DOCUMENTS
                </Button>
              </Box>
            </Box>
          ) : isContractorIpen ? (
            <Box>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <img
                  alt="avatar_circle"
                  src="/img/logos/logo_main.png"
                  style={{ width: "15rem", height: "auto", zIndex: 2 }}
                />
              </Box>
              {contractorNotarization?.require_ipen_pin ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    mt: 3,
                    px: 2,
                  }}
                >
                  <Typography
                    variant="h5"
                    component="h3"
                    align="center"
                    color="primary"
                    fontWeight="bold"
                    mb={2}
                  >
                    Your IPEN Verification PIN
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h2"
                      component="div"
                      align="center"
                      color="primary"
                      fontWeight="bold"
                      sx={{
                        fontSize: { xs: "2rem", sm: "3rem" },
                        letterSpacing: "0.5rem",
                        fontFamily: "monospace",
                        backgroundColor: "#f0f8ff",
                        border: "2px solid",
                        borderColor: "primary.main",
                        borderRadius: 2,
                        px: 4,
                        py: 2,
                      }}
                    >
                      {contractorIpenPin || "----"}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    align="center"
                    color="text.secondary"
                    sx={{ maxWidth: "500px" }}
                  >
                    Please provide this verification PIN to your assigned notary
                    when you meet in person for your appointment. The notary
                    will use this PIN to verify your identity and initiate the
                    electronic notarization session.
                  </Typography>
                  <Typography
                    variant="body2"
                    align="center"
                    color="text.secondary"
                    sx={{ maxWidth: "500px", mt: 2, fontStyle: "italic" }}
                  >
                    Keep this PIN confidential and only share it with your
                    assigned notary when you are together and ready to proceed.
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    mt: 3,
                    px: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      backgroundColor: "#f0fff4",
                      border: "2px solid",
                      borderColor: "success.main",
                      borderRadius: 2,
                      px: 4,
                      py: 3,
                      mb: 2,
                      maxWidth: "500px",
                      width: "100%",
                    }}
                  >
                    <CheckCircleOutlineIcon
                      color="success"
                      sx={{ fontSize: "3rem", mb: 1 }}
                    />
                    <Typography
                      variant="h5"
                      component="h3"
                      align="center"
                      color="success.main"
                      fontWeight="bold"
                    >
                      You're All Set
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    align="center"
                    color="text.secondary"
                    sx={{ maxWidth: "500px" }}
                  >
                    No verification PIN is required for your IPEN session. Your
                    assigned notary has everything they need to begin the
                    appointment.
                  </Typography>
                  <Typography
                    variant="body2"
                    align="center"
                    color="text.secondary"
                    sx={{ maxWidth: "500px", mt: 2, fontStyle: "italic" }}
                  >
                    Please have a valid government-issued photo ID ready when
                    you meet with your notary in person.
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <ScheduleRONComponent
              handleImmediateRON={handleImmediateRON}
              schedule={schedule}
              scheduledDate={scheduledDate}
              setScheduledDate={setScheduledDate}
              setSchedule={setSchedule}
              isScheduled={isScheduled}
              ronDate={ronDate}
              fiveMinBeforeSession={fiveMinBeforeSession}
              setOpen={setOpen}
              sessionUrl={sessionUrl}
              handleStartClick={handleStartClick}
              cancelRef={cancelRef}
              handleTimeUp={handleTimeUp}
              handleTimeUpCalledRef={handleTimeUpCalledRef}
              acceptanceInProgress={acceptanceInProgress}
              notarySource={notarySource}
              setNotarySource={setNotarySource}
              hasOrgNotaries={hasOrgNotaries}
              hasRonOrgNotaries={hasRonOrgNotaries}
              orgName={orgName}
              userTimezone={userTimezone}
            />
          )}
        </Box>
        <Box
          sx={{
            borderBottomLeftRadius: "18px",
            borderBottomRightRadius: "18px",
            backgroundColor: "#fff",
            position: "fixed",
            bottom: -20,
            left: 0,
            width: "100%",
            // borderTop: "3px solid #d3d3d3",
            py: 1,
            animation: "slideUp 0.5s ease-in-out",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
            <Typography
              sx={{ mt: 1, mb: 1, px: 2 }}
              fontWeight="bold"
              align="center"
              variant={tabletSize ? "body2" : "h6"}
              color="error"
            >
              {schedule?.submitError || schedule?.instantError}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default React.memo(ScheduleContractorRonModal);
